/**
 * A2hsPrompts — 로그인된 모바일 사용자에게 띄우는 안내 모달 2종의 판정·순서(Layout·GroupShell 이 한 번만 그린다).
 *
 *  ① 설치 안내(InstallPromptModal): 모바일 브라우저(iOS Safari · Android Chrome 계열) + 아직 앱 아님 + 안 닫음(기기 localStorage)
 *     → 로그인 뒤 스플래시가 걷힌 다음(1.2초) 1회. 내 계정 › 기기관리 [추가] 로도 연다(useA2hsGuide).
 *  ② 알림 안내(NotifyPromptModal): 홈 화면 앱(standalone) + 모바일 + 권한 아직 안 물음 + 안 봄 → 첫 진입 1회.
 *  두 모달은 같은 세션에서 겹치지 않는다(앱이면 ①이 없고, 브라우저면 ②가 없다).
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { closeA2hsGuide, useA2hsGuide } from "@/hooks/useA2hsGuide";
import { a2hsPlatformOf, notifyDemoPlatformOf, shouldOfferInstall, shouldOfferNotify, A2HS_IOS_DONE_MS, A2HS_LATER_MS, type A2hsPlatform } from "@/domain/a2hs";
import { detectDeviceKind, isStandaloneApp } from "@/domain/device_info";
import { readA2hsDismiss, readPushPromptSeen, writeA2hsDismiss, writePushPromptSeen } from "@/lib/a2hsLocal";
import { getPushState } from "@/services/messaging";
import { InstallPromptModal, type InstallCloseReason } from "./InstallPromptModal";
import { NotifyPromptModal } from "./NotifyPromptModal";

/** 로그인 뒤 스플래시(약 1초)가 걷힌 다음에 띄운다 */
const AUTO_DELAY_MS = 1200;

function currentPlatform(): A2hsPlatform | null {
  if (typeof navigator === "undefined") return null;
  return a2hsPlatformOf({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints, standalone: isStandaloneApp() });
}

export function A2hsPrompts() {
  const { user, loading, sharedSession } = useAuth();
  const guideOpen = useA2hsGuide();
  const [auto, setAuto] = useState<"install" | "notify" | null>(null);
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (loading || !uid) return; // 로그아웃 상태는 아래 `if (!user) return null` 이 가린다
    let alive = true;
    const t = window.setTimeout(() => {
      void (async () => {
        const platform = currentPlatform();
        if (platform && shouldOfferInstall(platform, readA2hsDismiss(), Date.now())) {
          if (alive) setAuto("install");
          return;
        }
        const mobile = detectDeviceKind({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints }) === "mo";
        if (!isStandaloneApp() || !mobile || readPushPromptSeen()) return;
        const env = await getPushState();
        if (!alive) return;
        if (shouldOfferNotify({ standalone: true, mobile, sharedSession, seen: false, supported: env.supported, permission: env.permission })) {
          writePushPromptSeen(); // 보여 준 순간 기록 — 새로고침·강제 종료로 다시 뜨지 않게
          setAuto("notify");
        }
      })();
    }, AUTO_DELAY_MS);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [uid, loading, sharedSession]);

  if (!user) return null;

  const platform = currentPlatform();
  const showInstall = (auto === "install" || guideOpen) && platform;

  function closeInstall(reason: InstallCloseReason) {
    const now = Date.now();
    if (reason === "never") writeA2hsDismiss({ kind: "never" });
    else if (reason === "later") writeA2hsDismiss({ kind: "later", until: now + A2HS_LATER_MS });
    else if (reason === "ios_done") writeA2hsDismiss({ kind: "later", until: now + A2HS_IOS_DONE_MS });
    else writeA2hsDismiss({ kind: "never" }); // installed — 앱에서는 어차피 안 뜨고, 브라우저에서도 더 묻지 않는다
    setAuto(null);
    closeA2hsGuide();
  }

  if (showInstall) return <InstallPromptModal platform={platform} onClose={closeInstall} />;
  if (auto === "notify") {
    return <NotifyPromptModal platform={notifyDemoPlatformOf(navigator.userAgent, navigator.maxTouchPoints)} onClose={() => setAuto(null)} />;
  }
  return null;
}
